import {
  Component,
  EventEmitter,
  OnInit,
  OnDestroy,
  Output,
  signal,
} from "@angular/core";
import { Subject, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { IClippingStudioMamCatalogService } from "app/js/src/domain/clipping-studio/i-clipping-studio-mam-catalog.service";
import { ClippingStudioMamItem } from "app/js/src/domain/clipping-studio/clipping-studio-mam-item";
import { ClippingStudioManagerService } from "app/js/src/infrastructure/clipping-studio/clipping-studio-manager.service";
import { TagRepository } from "app/js/src/domain/tag-repository";
import { Tag } from "app/js/src/domain/tag";
import { MediaRepository } from "app/js/src/domain/media-repository";

type SortMode = "recent" | "a-z" | "z-a";

export type MediaPanelLanguage = { code: string; name: string };

/** Target videos per fill-loop batch (API Limit is 20 raw rows). */
const VIDEO_PAGE_SIZE = 20;
const SCROLL_LOAD_THRESHOLD_PX = 120;

@Component({
  selector: "evrideo-clipping-studio-media-panel",
  templateUrl: "./clipping-studio-media-panel.component.html",
  styleUrls: ["./clipping-studio-media-panel.component.scss"],
})
export class ClippingStudioMediaPanelComponent implements OnInit, OnDestroy {
  searchTerm = "";
  sortBy: SortMode = "recent";
  mamVideoItems: ClippingStudioMamItem[] = [];
  mamLoading = false;
  mamLoadingMore = false;

  @Output() addToTimeline = new EventEmitter<ClippingStudioMamItem>();

  /** Languages from API (same source as Scheduler: getTenantMediasLanguages). */
  private readonly languagesSignal = signal<MediaPanelLanguage[]>([]);
  readonly languages = this.languagesSignal.asReadonly();

  selectedLanguages: MediaPanelLanguage[] = [];
  checkAllLanguages = true;

  /** Tags from API (same source as Scheduler: TagRepository.getGeneralTags). */
  private readonly tagsSignal = signal<Tag[]>([]);
  readonly tags = this.tagsSignal.asReadonly();

  private readonly selectedTagsSignal = signal<Tag[]>([]);
  readonly selectedTags = this.selectedTagsSignal.asReadonly();

  checkAllTags = true;

  /** Collapsed by default: show first 4 tags; toggle to expand/collapse (same as Scheduler). */
  private readonly showMoreTagsSignal = signal(false);
  readonly showMoreTags = this.showMoreTagsSignal.asReadonly();

  private mamSearchSubject = new Subject<string>();
  private mamSearchSub!: Subscription;
  private tagCreatedSub?: Subscription;
  private mediaAssociatedSub?: Subscription;

  private apiOffset = 0;
  private apiExhausted = false;
  private requestGeneration = 0;

  constructor(
    private mamCatalogService: IClippingStudioMamCatalogService,
    private manager: ClippingStudioManagerService,
    private tagRepository: TagRepository,
    private mediaRepository: MediaRepository,
  ) {}

  ngOnInit(): void {
    this.tagsSignal.set(this.tagRepository.getGeneralTags());
    this.tagCreatedSub = this.tagRepository.generalTagCreated.subscribe(
      (tag: Tag) => {
        this.tagsSignal.update((prev) => [...prev, tag]);
      },
    );
    this.updateTenantMediaLanguages();
    this.mediaAssociatedSub = this.mediaRepository.mediaAssociatedFilesUpdated.subscribe(
      () => this.updateTenantMediaLanguages(),
    );
    this.loadMamItems();
    this.mamSearchSub = this.mamSearchSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => this.loadMamItems());
  }

  ngOnDestroy(): void {
    this.mamSearchSub?.unsubscribe();
    this.tagCreatedSub?.unsubscribe();
    this.mediaAssociatedSub?.unsubscribe();
  }

  private updateTenantMediaLanguages(): void {
    this.mediaRepository.getTenantMediasLanguages().then((result) => {
      if (result.length > 0) {
        this.languagesSignal.set(result);
      }
    });
  }

  /** Filtered and sorted MAM items: tag and language filters are server-side; only sort applied on client. */
  get filteredMamItems(): ClippingStudioMamItem[] {
    const result = [...this.mamVideoItems];

    switch (this.sortBy) {
      case "a-z":
        result.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case "z-a":
        result.sort((a, b) => b.title.localeCompare(a.title));
        break;
      case "recent":
      default:
        result.sort((a, b) => {
          const ta = a.createdAt ?? 0;
          const tb = b.createdAt ?? 0;
          return tb - ta;
        });
        break;
    }

    return result;
  }

  onClickAllLanguages(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.checked) {
      this.selectedLanguages = [];
      this.checkAllLanguages = true;
      this.loadMamItems();
    }
  }

  onClickLanguage(event: Event, index: number): void {
    const target = event.target as HTMLInputElement;
    const lang = this.languages()[index];
    if (target.checked) {
      this.selectedLanguages = [...this.selectedLanguages, lang];
      this.checkAllLanguages = false;
    } else {
      this.selectedLanguages = this.selectedLanguages.filter((l) => l !== lang);
      if (this.selectedLanguages.length === 0) {
        this.checkAllLanguages = true;
      }
    }
    this.loadMamItems();
  }

  isLanguageChecked(index: number): boolean {
    const lang = this.languages()[index];
    return this.selectedLanguages.indexOf(lang) > -1;
  }

  onClickAllTags(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.checked) {
      this.selectedTagsSignal.set([]);
      this.checkAllTags = true;
      this.loadMamItems();
    }
  }

  onClickTag(event: Event, index: number): void {
    const target = event.target as HTMLInputElement;
    const tag = this.tags()[index];
    if (target.checked) {
      this.selectedTagsSignal.update((prev) => [...prev, tag]);
      this.checkAllTags = false;
    } else {
      this.selectedTagsSignal.update((prev) => prev.filter((t) => t !== tag));
      if (this.selectedTagsSignal().length === 0) {
        this.checkAllTags = true;
      }
    }
    this.loadMamItems();
  }

  isTagChecked(index: number): boolean {
    const tag = this.tags()[index];
    return this.selectedTags().indexOf(tag) > -1;
  }

  onToggleTags(show: boolean): void {
    this.showMoreTagsSignal.set(show);
  }

  onSearchTermChange(value: string): void {
    this.searchTerm = value;
    this.mamSearchSubject.next(value);
  }

  clearSearch(): void {
    this.searchTerm = "";
    this.mamSearchSubject.next("");
  }

  onMamListScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (!el) {
      return;
    }
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remaining <= SCROLL_LOAD_THRESHOLD_PX) {
      void this.loadMoreMamItems();
    }
  }

  /** Reloads from the start of the API cursor (search / tag / language change). */
  private async loadMamItems(): Promise<void> {
    this.requestGeneration += 1;
    const generation = this.requestGeneration;
    this.apiOffset = 0;
    this.apiExhausted = false;
    this.mamVideoItems = [];
    this.mamLoading = true;
    this.mamLoadingMore = false;

    try {
      await this.fetchNextVideoBatch(generation, false);
    } finally {
      if (generation === this.requestGeneration) {
        this.mamLoading = false;
      }
    }
  }

  private async loadMoreMamItems(): Promise<void> {
    if (
      this.mamLoading ||
      this.mamLoadingMore ||
      this.apiExhausted ||
      this.mamVideoItems.length === 0
    ) {
      return;
    }

    const generation = this.requestGeneration;
    this.mamLoadingMore = true;
    try {
      await this.fetchNextVideoBatch(generation, true);
    } finally {
      if (generation === this.requestGeneration) {
        this.mamLoadingMore = false;
      }
    }
  }

  private async fetchNextVideoBatch(
    generation: number,
    append: boolean,
  ): Promise<void> {
    const tagIds = this.selectedTagsSignal().map((t) => t.id);
    const languageCodes =
      this.checkAllLanguages || this.selectedLanguages.length === 0
        ? []
        : this.selectedLanguages.map((l) => l.code);

    try {
      const page = await this.mamCatalogService.getMamItemsForMedia(
        this.searchTerm,
        tagIds,
        languageCodes,
        this.apiOffset,
        VIDEO_PAGE_SIZE,
      );

      if (generation !== this.requestGeneration) {
        return;
      }

      this.apiOffset = page.nextOffset;
      this.apiExhausted = page.exhausted;
      this.mamVideoItems = append
        ? [...this.mamVideoItems, ...page.items]
        : page.items;
    } catch {
      if (generation === this.requestGeneration && !append) {
        this.mamVideoItems = [];
        this.apiExhausted = true;
      }
    }
  }

  /** Add MAM video to timeline: emit to parent so root can show loading dialog and wait for video load. */
  onAddToTimeline(item: ClippingStudioMamItem): void {
    if (item.mamMediaId == null || (item.durationMs ?? 0) <= 0) return;
    this.addToTimeline.emit(item);
  }

  get canAddToTimeline(): boolean {
    const state = this.manager.state;
    if (!state?.media) return false;
    return state.mode === "SOURCE" || state.mode === "EDIT";
  }
}
